import { Package, DollarSign, ShieldAlert, Sparkles, AlertOctagon, Check, MessageCircle, Bell, TrendingDown, ShoppingCart, Wallet, PackagePlus, Tag, User, FileText } from "lucide-react";

/**
 * Metadatos visuales por tipo de notificación:
 *  - icon: componente Lucide
 *  - label: nombre humano corto (usado como badge/categoria)
 *  - tone: paleta ("stock" | "precio" | "pedido" | "pago" | "excepcion" | "producto" | "comunicado" | "sistema")
 *  - accion: verbo sugerido en el CTA cuando la notif tiene target ("Ver pedido", "Abrir producto", etc)
 */
export const NOTIF_META = {
  // Stock
  "producto.agotado":       { icon: AlertOctagon,  label: "Agotado",       tone: "stock", accion: "Ver producto" },
  "producto.bajo_stock":    { icon: TrendingDown,  label: "Bajo stock",    tone: "stock", accion: "Ver producto" },
  "stock.agregado":         { icon: PackagePlus,   label: "Stock nuevo",   tone: "stock", accion: "Ver producto" },
  // Precios / costo
  "precio.sugerido.cambio": { icon: DollarSign,    label: "Precio sug.",   tone: "precio", accion: "Ver producto" },
  "precio.minimo.cambio":   { icon: ShieldAlert,   label: "Precio mín.",   tone: "precio", accion: "Ver producto" },
  "costo.cambio":           { icon: DollarSign,    label: "Costo",         tone: "precio", accion: "Ver producto" },
  // Productos
  "producto.creado":        { icon: Package,       label: "Producto",      tone: "producto", accion: "Abrir producto" },
  "producto.archivado":     { icon: Package,       label: "Archivado",     tone: "producto", accion: "Abrir producto" },
  "producto.reactivado":    { icon: Package,       label: "Reactivado",    tone: "producto", accion: "Abrir producto" },
  // Comisiones / políticas
  "regla_comision.cambio":  { icon: Sparkles,      label: "Comisiones",    tone: "sistema", accion: null },
  "regla_comision.creada":  { icon: Sparkles,      label: "Comisiones",    tone: "sistema", accion: null },
  "comisiones.liquidadas":  { icon: DollarSign,    label: "Comisión pagada", tone: "pago",  accion: "Ver detalle" },
  "comision.pendiente_liquidacion": { icon: DollarSign, label: "Comisión por pagar", tone: "pago", accion: "Ir a liquidar" },
  "politica.cambio":        { icon: Tag,           label: "Política",      tone: "sistema", accion: null },
  // Excepciones
  "excepcion.solicitada":   { icon: ShieldAlert,   label: "Excepción",     tone: "excepcion", accion: "Revisar" },
  "excepcion.aprobada":     { icon: Check,         label: "Aprobada",      tone: "excepcion", accion: "Ver detalle" },
  "excepcion.rechazada":    { icon: AlertOctagon,  label: "Rechazada",     tone: "excepcion", accion: "Ver detalle" },
  // Pagos
  "pago.registrado":        { icon: Wallet,        label: "Pago",          tone: "pago", accion: "Ver pago" },
  "pago.aprobado":          { icon: Check,         label: "Pago OK",       tone: "pago", accion: "Ver pago" },
  "pago.rechazado":         { icon: AlertOctagon,  label: "Pago rech.",    tone: "pago", accion: "Ver pago" },
  // Pedidos
  "pedido.creado":          { icon: ShoppingCart,  label: "Nuevo pedido",  tone: "pedido", accion: "Ver pedido" },
  "pedido.confirmado":      { icon: ShoppingCart,  label: "Confirmado",    tone: "pedido", accion: "Ver pedido" },
  "pedido.despachado":      { icon: ShoppingCart,  label: "Despachado",    tone: "pedido", accion: "Ver pedido" },
  "pedido.entregado":       { icon: ShoppingCart,  label: "Entregado",     tone: "pedido", accion: "Ver pedido" },
  "pedido.cancelado":       { icon: ShoppingCart,  label: "Cancelado",     tone: "pedido", accion: "Ver pedido" },
  // Comunicados
  "comunicado.director":    { icon: MessageCircle, label: "Comunicado",    tone: "comunicado", accion: null },
};

export const NOTIF_FALLBACK = { icon: Bell, label: "Aviso", tone: "sistema", accion: null };

/**
 * Devuelve las clases Tailwind para el "tono" visual de la notificación.
 * chip: fondo del badge de categoria
 * dot: puntito de estado
 * ring: para bordes / anillos
 * iconBg / iconFg: para el círculo del icono
 */
export function toneClasses(tone, critica = false) {
  const palettes = {
    stock:     { chip: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300", iconBg: "bg-amber-500/15", iconFg: "text-amber-600 dark:text-amber-400", accent: "border-amber-400" },
    precio:    { chip: "bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-300", iconBg: "bg-rose-500/15", iconFg: "text-rose-600 dark:text-rose-400", accent: "border-rose-400" },
    pedido:    { chip: "bg-blue-100 text-blue-800 dark:bg-blue-500/15 dark:text-blue-300", iconBg: "bg-blue-500/15", iconFg: "text-blue-600 dark:text-blue-400", accent: "border-blue-400" },
    pago:      { chip: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300", iconBg: "bg-emerald-500/15", iconFg: "text-emerald-600 dark:text-emerald-400", accent: "border-emerald-400" },
    excepcion: { chip: "bg-purple-100 text-purple-800 dark:bg-purple-500/15 dark:text-purple-300", iconBg: "bg-purple-500/15", iconFg: "text-purple-600 dark:text-purple-400", accent: "border-purple-400" },
    producto:  { chip: "bg-teal-100 text-teal-800 dark:bg-teal-500/15 dark:text-teal-300", iconBg: "bg-teal-500/15", iconFg: "text-teal-600 dark:text-teal-400", accent: "border-teal-400" },
    comunicado:{ chip: "bg-primary/15 text-primary", iconBg: "bg-primary/15", iconFg: "text-primary", accent: "border-primary" },
    sistema:   { chip: "bg-muted text-muted-foreground", iconBg: "bg-muted", iconFg: "text-muted-foreground", accent: "border-muted-foreground" },
  };
  const p = palettes[tone] || palettes.sistema;
  if (critica) {
    return { ...p, chip: "bg-rose-500 text-white", iconBg: "bg-rose-500/20", iconFg: "text-rose-600" };
  }
  return p;
}

export function getNotifMeta(tipo) {
  return NOTIF_META[tipo] || NOTIF_FALLBACK;
}

/**
 * Devuelve un tiempo humanizado ("hace 5 min", "ayer", "22 de febrero").
 * Sin dependencia de librerías: pura JS.
 */
export function humanTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000; // segundos
    if (diff < 60) return "justo ahora";
    if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
    if (diff < 172800) return "ayer";
    if (diff < 604800) return `hace ${Math.floor(diff / 86400)} días`;
    return d.toLocaleDateString("es-CO", { day: "numeric", month: "short" });
  } catch { return ""; }
}

/**
 * Agrupa una lista de notificaciones por rango temporal.
 * Devuelve un array de {label, items}.
 */
export function agruparPorTiempo(items) {
  const ahora = Date.now();
  const dia = 86400 * 1000;
  const buckets = { hoy: [], ayer: [], semana: [], antes: [] };
  items.forEach((n) => {
    const t = new Date(n.at || n.created_at).getTime();
    const diff = ahora - t;
    if (diff < dia) buckets.hoy.push(n);
    else if (diff < 2 * dia) buckets.ayer.push(n);
    else if (diff < 7 * dia) buckets.semana.push(n);
    else buckets.antes.push(n);
  });
  const out = [];
  if (buckets.hoy.length) out.push({ label: "Hoy", items: buckets.hoy });
  if (buckets.ayer.length) out.push({ label: "Ayer", items: buckets.ayer });
  if (buckets.semana.length) out.push({ label: "Esta semana", items: buckets.semana });
  if (buckets.antes.length) out.push({ label: "Anteriores", items: buckets.antes });
  return out;
}

/**
 * Ruta destino según entity_type. `null` si no hay destino navegable.
 *
 * Para pagos: la notif abre DIRECTAMENTE el comprobante del pago en /pagos.
 * Solo si no hay pago id fallback al pedido o al listado.
 */
export function routeForNotif(n) {
  const et = n.entity_type;
  const id = n.entity_id;
  const pedidoRelacionado = n?.data?.pedido_id;
  if (!et) return null;
  if (et === "pedido" && id) return `/pedidos?open=${id}`;
  if (et === "pago") {
    // Preferimos abrir el comprobante en /pagos si tenemos el pago_id.
    if (id) return `/pagos?open=${id}`;
    if (pedidoRelacionado) return `/pedidos?open=${pedidoRelacionado}`;
    return `/pagos`;
  }
  // Liquidación de comisión: abrir Mis Comisiones con el batch resaltado + opción de descargar
  if (et === "liquidacion" && id) return `/mis-comisiones?batch=${id}`;
  if (et === "excepcion") return `/excepciones`;
  if (et === "producto" && id) return `/productos?highlight=${id}`;
  if (et === "contenedor") return `/productos`;
  if (et === "cliente" && id) return `/clientes?highlight=${id}`;
  if (et === "remision" && id) return `/remisiones`;
  if (et === "user" || et === "usuario") return `/equipo`;
  return null;
}
